/**
 * NanoHUB/KMFDDM API (Frontend).
 * Ruft Xavier `/api/ddm` auf, das an NanoHUB `/api/v1/ddm` weiterreicht.
 */

import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/api/ddm`;

const getAuthHeader = () => {
  const tokenStr = localStorage.getItem('user');
  if (tokenStr) {
    const token = JSON.parse(tokenStr).token;
    return { Authorization: `Bearer ${token}` };
  }
  return {};
};

function qs(params = {}) {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === false || value === '') continue;
    usp.append(key, value === true ? '1' : String(value));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

async function ddmGet(path, params) {
  const { data } = await axios.get(`${API_URL}${path}${qs(params)}`, { headers: getAuthHeader() });
  return data;
}

async function ddmPut(path, body, params) {
  const { data } = await axios.put(`${API_URL}${path}${qs(params)}`, body || {}, {
    headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
  });
  return data;
}

async function ddmPost(path, body, params) {
  const { data } = await axios.post(`${API_URL}${path}${qs(params)}`, body || {}, {
    headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
  });
  return data;
}

async function ddmDelete(path, params) {
  const { data } = await axios.delete(`${API_URL}${path}${qs(params)}`, { headers: getAuthHeader() });
  return data;
}

export async function getDdmStatus() {
  return ddmGet('/status');
}

export async function getDeclarations({ detail = false } = {}) {
  return ddmGet('/declarations', { detail: detail ? '1' : undefined });
}

export async function getDeclaration(identifier) {
  return ddmGet(`/declarations/${encodeURIComponent(identifier)}`);
}

export async function putDeclaration(declaration, { noNotify = false } = {}) {
  return ddmPut('/declarations', declaration, { nonotify: noNotify || undefined });
}

export async function deleteDeclaration(identifier) {
  return ddmDelete(`/declarations/${encodeURIComponent(identifier)}`);
}

export async function touchDeclaration(identifier, { noNotify = false } = {}) {
  return ddmPost(`/declarations/${encodeURIComponent(identifier)}/touch`, {}, {
    nonotify: noNotify || undefined,
  });
}

export async function getDeclarationSets(identifier) {
  return ddmGet(`/declarations/${encodeURIComponent(identifier)}/sets`);
}

export async function getSets() {
  return ddmGet('/sets');
}

export async function getSetDeclarations(setName) {
  return ddmGet(`/sets/${encodeURIComponent(setName)}/declarations`);
}

export async function associateSetDeclaration(setName, declaration, { noNotify = false } = {}) {
  return ddmPut(`/sets/${encodeURIComponent(setName)}/declarations`, { declaration }, {
    nonotify: noNotify || undefined,
  });
}

export async function dissociateSetDeclaration(setName, declaration, { noNotify = false } = {}) {
  return ddmDelete(`/sets/${encodeURIComponent(setName)}/declarations`, {
    declaration,
    nonotify: noNotify || undefined,
  });
}

export async function getEnrollmentSets(udid) {
  return ddmGet(`/enrollments/${encodeURIComponent(udid)}/sets`);
}

export async function associateEnrollmentSet(udid, setName, { noNotify = false } = {}) {
  return ddmPut(`/enrollments/${encodeURIComponent(udid)}/sets`, { set: setName }, {
    nonotify: noNotify || undefined,
  });
}

export async function dissociateEnrollmentSet(udid, setName, { noNotify = false } = {}) {
  return ddmDelete(`/enrollments/${encodeURIComponent(udid)}/sets`, {
    set: setName,
    nonotify: noNotify || undefined,
  });
}

export async function dissociateAllEnrollmentSets(udid, { noNotify = false } = {}) {
  return ddmDelete(`/enrollments/${encodeURIComponent(udid)}/sets`, {
    nonotify: noNotify || undefined,
  });
}

export async function getEnrollmentDeclarationStatus(udid) {
  return ddmGet(`/enrollments/${encodeURIComponent(udid)}/declaration-status`);
}

export async function getEnrollmentStatusErrors(udid) {
  return ddmGet(`/enrollments/${encodeURIComponent(udid)}/status-errors`);
}

export async function clearEnrollmentStatusErrors(udid) {
  return ddmDelete(`/enrollments/${encodeURIComponent(udid)}/status-errors`);
}

export async function getEnrollmentStatusReport(udid, { index, statusId } = {}) {
  return ddmGet(`/enrollments/${encodeURIComponent(udid)}/status-report`, {
    index,
    status_id: statusId,
  });
}

export async function getEnrollmentStatusValues(udid, { prefix } = {}) {
  return ddmGet(`/enrollments/${encodeURIComponent(udid)}/status-values`, { prefix });
}

export async function getEnrollmentDdmSummary(udid) {
  return ddmGet(`/enrollments/${encodeURIComponent(udid)}/summary`);
}

export async function enableEnrollmentDdm(udid) {
  return ddmPost(`/enrollments/${encodeURIComponent(udid)}/enable`, {});
}

export async function notifyEnrollmentDdm(udid) {
  return ddmPost(`/enrollments/${encodeURIComponent(udid)}/notify`, {});
}

export async function notifyDdm({ ids, sets, declarations } = {}) {
  return ddmPost('/notify', { ids, sets, declarations });
}

export async function getDdmSchemaCatalog({ family = 'configurations', prefix, refresh = false } = {}) {
  return ddmGet('/schema/declarations', {
    family,
    prefix,
    refresh: refresh ? '1' : undefined,
  });
}

export async function getDdmSchema(declarationType, { refresh = false } = {}) {
  return ddmGet(`/schema/declarations/${encodeURIComponent(declarationType)}`, {
    refresh: refresh ? '1' : undefined,
  });
}

export async function getLegacyProfileUrl(profileId) {
  return ddmGet(`/legacy-profiles/${encodeURIComponent(profileId)}`);
}

const nanoDdmService = {
  getStatus: getDdmStatus,
  getDeclarations,
  getDeclaration,
  putDeclaration,
  deleteDeclaration,
  touchDeclaration,
  getDeclarationSets,
  getSets,
  getSetDeclarations,
  associateSetDeclaration,
  dissociateSetDeclaration,
  getEnrollmentSets,
  associateEnrollmentSet,
  dissociateEnrollmentSet,
  dissociateAllEnrollmentSets,
  getEnrollmentDeclarationStatus,
  getEnrollmentStatusErrors,
  clearEnrollmentStatusErrors,
  getEnrollmentStatusReport,
  getEnrollmentStatusValues,
  getEnrollmentDdmSummary,
  enableEnrollmentDdm,
  notifyEnrollmentDdm,
  notifyDdm,
  getDdmSchemaCatalog,
  getDdmSchema,
  getLegacyProfileUrl,
};
export default nanoDdmService;
